<template>
    <div class="dashboard">
        <!-- Top bar -->
        <div class="topbar">
            <div class="topbar-title">
                <h1 class="head">Dashboard</h1>
            </div>

            <div class="topbar-meta">
                <div class="loading-indicator" v-if="loadingDashboard">
                    <loader size="small"></loader>
                </div>

                <div class="d-flex flex-row align-items-center mr-2" v-if="selectedTimeOption.value === 'custom'">
                    <i class="ico ico-calendar fs-large mr-2 accent-icon"></i>

                    <date-picker-new class="dateRangePicker" :class="{ 'is-disabled': loadingDashboard }"
                        :key="pickerKey" v-model="customDate" :range="true" :clearOutside="true" :showFromTo="false"
                        @input="dateRangeChanged">
                    </date-picker-new>
                </div>

                <div class="range-pill" :class="{ 'is-disabled': loadingDashboard }">
                    <button v-for="opt in timeOptions" :key="opt.value" :disabled="loadingDashboard"
                        :class="{ active: selectedTimeOption.value === opt.value }" @click="selectRange(opt)">
                        {{ opt.label }}
                    </button>
                </div>
            </div>
        </div>

        <!-- KPI row -->
        <div class="kpi-row">
            <kpiCard v-for="kpi in kpis" :key="kpi.label" :accent="kpi.accent" :label="kpi.label" :value="kpi.value"
                :deltaType="kpi.deltaType">
            </kpiCard>
        </div>

        <!-- Secondary grid -->
        <div class="sec-grid">
            <channelMix :channels="channels"></channelMix>
            <topAgents :agents="agents"></topAgents>
            <topExperiences :experiences="experiences"></topExperiences>
            <guestOrigin :origins="origins"></guestOrigin>
        </div>

        <!-- Reports Center -->
        <reportsCenter :subTitle="cardLabel" :dateFrom="pickerDateFrom" :dateTo="pickerDateTo"></reportsCenter>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

import kpiCard from './kpiCard'
import channelMix from './channelMix'
import topExperiences from './topExperiences'
import guestOrigin from './guestOrigin'
import topAgents from './topAgents'
import reportsCenter from './reportsCenter'
import DatePickerNew from '@/tools/DatePicker'
import Loader from '@/tools/loaders/loader1'

export default {
    data() {
        return {
            timeOptions: [
                { label: 'Today', value: 'today' },
                { label: 'Week', value: 'week' },
                { label: 'Month', value: 'month' },
                { label: 'Custom', value: 'custom' }
            ],
            selectedTimeOption: { label: 'Today', value: 'today' },
            dateFrom: moment().startOf('day'),
            dateTo: moment().endOf('day'),
            customDate: [],
            pickerKey: 0,
            dashboardData: null,
            loadingDashboard: false,
            channelPalette: ['#0E7C9E', '#E8A33D', '#0B1826', '#4C9A6A', '#E15B4D', '#8E44AD', '#2A9D8F']
        }
    },
    created() {
        this.getDashboardData();
    },
    computed: {
        ...mapGetters(['account']),
        todayLabel() {
            return moment().format('ddd, MMM D');
        },
        pickerDateFrom() {
            return moment(this.dateFrom).format('YYYY-MM-DD');
        },
        pickerDateTo() {
            return moment(this.dateTo).format('YYYY-MM-DD');
        },
        cardLabel() {
            return moment(this.dateFrom).format('MMMM');
        },
        kpis() {
            const d = this.dashboardData;

            return [
                {
                    label: 'Total Sales',
                    value: d ? d.totalSales : '—',
                    accent: 'ocean'
                },
                {
                    label: 'Bookings',
                    value: d ? d.numberOfBookings : '—',
                    accent: 'ink'
                },
                {
                    label: 'Bookings In Due',
                    value: d ? d.inDueBookings : '—',
                    accent: 'moss'
                },
                {
                    label: 'Occupancy Rate',
                    value: d ? d.occupancyRate : '—',
                    accent: 'amber'
                },
                {
                    label: 'Cancellation Rate',
                    value: d ? d.cancellationRate : '—',
                    accent: 'coral'
                },
            ];
        },
        origins() {
            const countries = (this.dashboardData && this.dashboardData.topCountries) || [];
            const totalCount = countries.reduce((sum, c) => sum + c.total, 0);

            return countries
                .slice()
                .sort((a, b) => b.total - a.total)
                .map(c => ({
                    name: c.name,
                    total: c.total,
                    pct: totalCount ? Math.round((c.total / totalCount) * 1000) / 10 : 0,
                    code: this.countryCode(c.name)
                }));
        },
        channels() {
            const list = (this.dashboardData && this.dashboardData.topChannels) || [];
            const totalCount = list.reduce((sum, c) => sum + c.total, 0);

            return list
                .slice()
                .sort((a, b) => b.total - a.total)
                .map((c, index) => ({
                    name: c.name,
                    total: c.total,
                    pct: totalCount ? Math.round((c.total / totalCount) * 1000) / 10 : 0,
                    color: this.channelColor(c.name, index)
                }));
        },
        agents() {
            const list = (this.dashboardData && this.dashboardData.topAgents) || [];

            return list
                .slice()
                .sort((a, b) => b.total - a.total)
                .map(a => ({
                    name: a.name,
                    sales: a.total
                }));
        },
        experiences() {
            const list = (this.dashboardData && this.dashboardData.topExperiences) || [];

            return list.map(e => ({
                name: e.name,
                bookings: e.bookings,
                sales: e.sales
            }));
        }
    },
    methods: {
        countryCode(name) {
            if (!name) return '—';
            return name.trim().slice(0, 2).toUpperCase();
        },
        channelColor(name, index) {
            return this.channelPalette[index % this.channelPalette.length];
        },
        getDashboardData() {
            if (this.loadingDashboard) {
                return;
            }

            this.loadingDashboard = true;

            axios.get('/api/supplier/Dashboard', {
                params: {
                    'query.dateStart': this.pickerDateFrom,
                    'query.dateEnd': this.pickerDateTo
                }
            }).then(response => {
                if (response.status == 200) {
                    this.dashboardData = response.data;
                }
            }).catch(error => {
            }).finally(() => {
                this.loadingDashboard = false;
            });
        },

        selectRange(opt) {
            this.selectedTimeOption = opt;

            let newDateFrom = null;
            let newDateTo = null;

            switch (opt.value) {
                case 'today':
                    newDateFrom = moment().startOf('day');
                    newDateTo = moment().endOf('day');
                    break;
                case 'week':
                    newDateFrom = moment().startOf('week');
                    newDateTo = moment().endOf('week');
                    break;
                case 'month':
                    newDateFrom = moment().startOf('month');
                    newDateTo = moment().endOf('month');
                    break;
                case 'custom':
                    newDateFrom = moment().startOf('month');
                    newDateTo = moment().endOf('month');

                    this.customDate = [newDateFrom.format('YYYY-MM-DD'), newDateTo.format('YYYY-MM-DD')];
                    this.pickerKey++;
                    break;
                default:
                    newDateFrom = moment().startOf('day');
                    newDateTo = moment().endOf('day');
            }

            this.dateFrom = newDateFrom;
            this.dateTo = newDateTo;
            this.getDashboardData();
        },
        dateRangeChanged() {
            if (!this.customDate || !this.customDate[0] || !this.customDate[1]) {
                return;
            }

            this.dateFrom = moment(this.customDate[0]);
            this.dateTo = moment(this.customDate[1]);
            this.getDashboardData();
        }
    },
    components: {
        kpiCard,
        channelMix,
        topExperiences,
        guestOrigin,
        topAgents,
        reportsCenter,
        DatePickerNew,
        Loader
    }
}
</script>

<style lang="scss" scoped>
.dashboard {
    --ink: #0B1826;
    --ink-soft: rgba(11, 24, 38, .56);
    --ink-faint: rgba(11, 24, 38, .36);
    --paper: #F1F4F6;
    --surface: #FFFFFF;
    --line: rgba(11, 24, 38, .08);
    --ocean: #0E7C9E;
    --ocean-soft: #E3F1F5;
    --amber: #E8A33D;
    --amber-soft: #FBF0DD;
    --coral: #E15B4D;
    --coral-soft: #FBE9E6;
    --moss: #4C9A6A;
    --moss-soft: #E7F3EC;
    --flap: #0F1F2C;
    --flap-line: rgba(255, 255, 255, .08);
    --radius: 14px;
    --shadow: 0 1px 2px rgba(11, 24, 38, .04), 0 6px 20px rgba(11, 24, 38, .05);

    display: flex;
    flex-direction: column;
    background: var(--paper);
    color: var(--ink);
    padding: 1.75rem 2rem 3rem;
    height: 100%;
    max-height: 100vh;
    overflow-y: auto;
    font-family: 'Inter', sans-serif;

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: var(--line);
        border-radius: 4px;

        &:hover {
            background: var(--ink-faint);
        }
    }
}

.head {
    font-family: 'Space Grotesk', sans-serif;
}

.topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    gap: 1rem;
    flex-wrap: wrap;
}

.topbar-title {
    display: flex;
    align-items: baseline;
    gap: .75rem;
}

.topbar-title h1 {
    font-size: 1.5rem;
    margin: 0;
    font-weight: 700;
    letter-spacing: -.01em;
}

.topbar-meta {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.loading-indicator {
    display: flex;
    align-items: center;
}

.dateRangePicker {
    min-width: 250px;

    &.is-disabled {
        opacity: .5;
        pointer-events: none;
    }
}

.accent-icon {
    color: var(--ocean);
}

.range-pill {
    display: flex;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: .25rem;
    box-shadow: var(--shadow);
    transition: opacity .15s ease;

    &.is-disabled {
        opacity: .6;
    }

    button {
        border: none;
        background: transparent;
        padding: .4rem .85rem;
        border-radius: 7px;
        font-family: 'Inter';
        font-size: .8rem;
        font-weight: 600;
        color: var(--ink-soft);
        cursor: pointer;
        transition: background .15s ease, color .15s ease;

        &:disabled {
            cursor: not-allowed;
        }

        &.active {
            background: var(--ink);
            color: #fff;
        }
    }
}

.kpi-row {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 1rem;
    margin-bottom: 1.25rem;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 700px) {
        grid-template-columns: repeat(2, 1fr);
    }
}

.sec-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.25rem;
    margin-bottom: 1.25rem;

    @media (max-width: 1400px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
}
</style>


// WEBPACK FOOTER //
// dashboard.vue?78d97222